import graphql__a48fa39fde79d48d1867 from "./__generated__/InterviewControlsSection_answer.graphql.js";import { useCallback, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { defineMessages } from "react-intl";

import { useFormatMessage } from "../Intl/Context";
import { oralInterviewMutationFeedback } from "./oralInterviewMutationUtils";
import {
  MESSAGE_ROLE_USER,
  INTERVIEW_STATUS_IN_PROGRESS,
  INTERVIEW_STATUS_PAUSED,
  INTERVIEW_UI_STATE_MIC_CHECK,
  INTERVIEW_UI_STATE_READY_TO_SPEAK,
  INTERVIEW_UI_STATE_RECORDING,
  INTERVIEW_UI_STATE_THINKING,
  INTERVIEW_UI_STATE_TRANSCRIBING
} from "./oralInterviewUiTransitions";
import Button from "../Buttons/Button";
import Spinner from "../Utils/Spinner";
import InterviewStateCard from "./InterviewStateCard";

import { completeOralInterview } from "../../mutations/CompleteOralInterviewMutation";
import { pauseOralInterview } from "../../mutations/PauseOralInterviewMutation";
import { resumeOralInterview } from "../../mutations/ResumeOralInterviewMutation";

const InterviewControlsSection = ({
  resourceId,
  interviewUiState,
  onInterviewUiStateChange,
  isBusy,
  onBusyStateChange,
  playCompletionAudio,
  stopAssistantAudioPlayback,
  ...props
}) => {
  const { status, messages, formField } = useFragment(
    InterviewControlsSection_answer,
    props.answer
  );
  const t = useFormatMessage();
  const pausedTitle = t(intlMessages.interviewPaused);
  const pausedDescription = t(intlMessages.interviewPausedDescription);
  const pauseLabel = t(intlMessages.pauseInterview);
  const resumeLabel = t(intlMessages.resumeInterview);
  const completeLabel = t(intlMessages.completeInterview);
  const genericErrorMessage = t(intlMessages.genericError);
  const networkErrorMessage = t(intlMessages.networkError);
  const pendingPauseLabel = t(intlMessages.pendingPauseInterview);
  const pendingResumeLabel = t(intlMessages.pendingResumeInterview);
  const pendingCompleteLabel = t(intlMessages.pendingCompleteInterview);

  const [pendingLabel, setPendingLabel] = useState(null);
  const [feedbackMessage, setFeedbackMessage] = useState(null);

  const isInProgress = status === INTERVIEW_STATUS_IN_PROGRESS;
  const isPaused = status === INTERVIEW_STATUS_PAUSED;
  const isRecorderActive =
    interviewUiState === INTERVIEW_UI_STATE_RECORDING ||
    interviewUiState === INTERVIEW_UI_STATE_TRANSCRIBING;
  const isMicCheckOpen = interviewUiState === INTERVIEW_UI_STATE_MIC_CHECK;
  const isThinking = interviewUiState === INTERVIEW_UI_STATE_THINKING;
  const hasUserMessage = messages.some(
    ({ role, text }) => role === MESSAGE_ROLE_USER && text?.trim().length > 0
  );

  const canPauseInterview =
    isInProgress && !isBusy && !isThinking && !isRecorderActive && !isMicCheckOpen;
  const canResumeInterview = isPaused && !isBusy;
  const canCompleteInterview =
    (isInProgress || isPaused) &&
    !isBusy &&
    !isRecorderActive &&
    !isMicCheckOpen &&
    (!formField.required || hasUserMessage);

  const clearPendingState = useCallback(() => {
    onBusyStateChange(false);
    setPendingLabel(null);
  }, [onBusyStateChange]);

  const setPendingState = useCallback(
    label => {
      onBusyStateChange(true);
      setPendingLabel(label);
      setFeedbackMessage(null);
    },
    [onBusyStateChange]
  );

  const handlePauseCompleted = useCallback(
    response => {
      clearPendingState();

      const result = response?.pauseOralInterview;
      setFeedbackMessage(oralInterviewMutationFeedback(result, genericErrorMessage));
    },
    [clearPendingState, genericErrorMessage]
  );

  const handleResumeCompleted = useCallback(
    response => {
      clearPendingState();

      const result = response?.resumeOralInterview;
      const mutationFeedbackMessage = oralInterviewMutationFeedback(
        result,
        genericErrorMessage
      );

      setFeedbackMessage(mutationFeedbackMessage);
      if (!mutationFeedbackMessage) {
        onInterviewUiStateChange(INTERVIEW_UI_STATE_READY_TO_SPEAK);
      }
    },
    [clearPendingState, genericErrorMessage, onInterviewUiStateChange]
  );

  const handleCompleteCompleted = useCallback(
    response => {
      clearPendingState();

      const result = response?.completeOralInterview;
      const mutationFeedbackMessage = oralInterviewMutationFeedback(
        result,
        genericErrorMessage
      );

      setFeedbackMessage(mutationFeedbackMessage);
      if (!mutationFeedbackMessage) playCompletionAudio(result);
    },
    [clearPendingState, genericErrorMessage, playCompletionAudio]
  );

  const handleMutationFailed = useCallback(() => {
    clearPendingState();
    setFeedbackMessage(networkErrorMessage);
  }, [clearPendingState, networkErrorMessage]);

  const handleInterviewPaused = useCallback(() => {
    if (!canPauseInterview || !resourceId || !formField.id) return;

    stopAssistantAudioPlayback();
    onInterviewUiStateChange(INTERVIEW_UI_STATE_READY_TO_SPEAK);
    setPendingState(pendingPauseLabel);
    pauseOralInterview(
      { resourceId, formFieldId: formField.id },
      handlePauseCompleted,
      handleMutationFailed
    );
  }, [
    canPauseInterview,
    formField.id,
    handleMutationFailed,
    handlePauseCompleted,
    onInterviewUiStateChange,
    pendingPauseLabel,
    resourceId,
    setPendingState,
    stopAssistantAudioPlayback
  ]);

  const handleInterviewResumed = useCallback(() => {
    if (!canResumeInterview || !resourceId || !formField.id) return;

    setPendingState(pendingResumeLabel);
    resumeOralInterview(
      { resourceId, formFieldId: formField.id },
      handleResumeCompleted,
      handleMutationFailed
    );
  }, [
    canResumeInterview,
    formField.id,
    handleMutationFailed,
    handleResumeCompleted,
    pendingResumeLabel,
    resourceId,
    setPendingState
  ]);

  const handleInterviewCompleted = useCallback(() => {
    if (!canCompleteInterview || !resourceId || !formField.id) return;

    stopAssistantAudioPlayback();
    setPendingState(pendingCompleteLabel);
    completeOralInterview(
      { resourceId, formFieldId: formField.id },
      handleCompleteCompleted,
      handleMutationFailed
    );
  }, [
    canCompleteInterview,
    formField.id,
    handleCompleteCompleted,
    handleMutationFailed,
    pendingCompleteLabel,
    resourceId,
    setPendingState,
    stopAssistantAudioPlayback
  ]);

  const pauseToggleHandler = isPaused ? handleInterviewResumed : handleInterviewPaused;
  const pauseToggleEnabled = isPaused ? canResumeInterview : canPauseInterview;
  const pauseToggleLabel = isPaused ? resumeLabel : pauseLabel;

  return (
    <>
      {isPaused && (
        <InterviewStateCard
          state="paused"
          className="oral-interview-paused margin-bottom-sm"
        >
          <h4 className="oral-interview-paused__title">{pausedTitle}</h4>
          <p className="oral-interview-paused__description">{pausedDescription}</p>
        </InterviewStateCard>
      )}

      <div className="oral-interview-actions">
        <Button
          type="button"
          className="btn btn-primary-inverse margin-right-xxs"
          onClick={pauseToggleHandler}
          disabled={!pauseToggleEnabled}
          label={pauseToggleLabel}
        />

        <Button
          type="button"
          className="btn btn-primary-inverse"
          onClick={handleInterviewCompleted}
          disabled={!canCompleteInterview}
          label={completeLabel}
        />
      </div>

      {pendingLabel && (
        <p className="margin-top-xs">
          <Spinner className="inline-spinner margin-right-xxs" />
          {pendingLabel}
        </p>
      )}

      {feedbackMessage && (
        <p className="form-text text-danger margin-top-xs">{feedbackMessage}</p>
      )}
    </>
  );
};

const InterviewControlsSection_answer = graphql__a48fa39fde79d48d1867;

export default InterviewControlsSection;

const intlMessages = defineMessages({
  completeInterview: {
    id: "answer.oralInterview.completeInterview",
    defaultMessage: "Terminer l'entretien"
  },
  genericError: {
    id: "answer.oralInterview.genericError",
    defaultMessage: "L'entretien vocal n'a pas pu être mis à jour."
  },
  interviewPaused: {
    id: "answer.oralInterview.interviewPaused",
    defaultMessage: "Entretien en pause"
  },
  interviewPausedDescription: {
    id: "answer.oralInterview.interviewPausedDescription",
    defaultMessage:
      "L'entretien est temporairement interrompu. Vous pouvez revenir à tout moment " +
      "pour le reprendre là où vous l'avez laissé."
  },
  networkError: {
    id: "answer.oralInterview.networkError",
    defaultMessage: "Une erreur réseau est survenue. Merci de réessayer."
  },
  pauseInterview: {
    id: "answer.oralInterview.pauseInterview",
    defaultMessage: "Mettre en pause"
  },
  pendingCompleteInterview: {
    id: "answer.oralInterview.pendingCompleteInterview",
    defaultMessage: "Clôture de l'entretien en cours..."
  },
  pendingPauseInterview: {
    id: "answer.oralInterview.pendingPauseInterview",
    defaultMessage: "Mise en pause..."
  },
  pendingResumeInterview: {
    id: "answer.oralInterview.pendingResumeInterview",
    defaultMessage: "Reprise de l'entretien..."
  },
  resumeInterview: {
    id: "answer.oralInterview.resumeInterview",
    defaultMessage: "Reprendre"
  }
});
